import React, { useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  FiDatabase,
  FiGlobe,
  FiLock,
  FiPlus,
  FiSearch,
} from "react-icons/fi";
import { fetchBuckets, createBucket, deleteBucket } from "../../services/bucketService";
import BucketCard from "./BucketCard";
import CreateBucketModal from "./modals/CreateBucketModal";
import EditBucketModal from "./modals/EditBucketModal";
import ConfirmDeleteModal from "./modals/ConfirmDeleteModal";
import ToastNotification from "./ToastNotification";
import "./Buckets.css";

function toBytes(bucket) {
  if (typeof bucket?.totalSize === "number") return bucket.totalSize;
  return 0;
}

export default function Buckets() {
  const [buckets, setBuckets] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editBucket, setEditBucket] = useState(null);
  const [confirmDeleteShow, setConfirmDeleteShow] = useState(false);
  const [bucketToDelete, setBucketToDelete] = useState(null);
  const [toastShow, setToastShow] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const loadBuckets = async () => {
    setLoading(true);
    try {
      const data = await fetchBuckets();
      setBuckets(data);
    } catch (err) {
      console.error("Erro ao listar buckets:", err);
      setToastMessage(`Não foi possível listar buckets: ${err.message}`);
      setToastShow(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBuckets();
  }, []);

  const filtered = useMemo(
    () =>
      buckets.filter((bucket) =>
        bucket.name.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [buckets, searchTerm]
  );

  const publicCount = buckets.filter((bucket) => !bucket.privado).length;
  const privateCount = buckets.length - publicCount;
  const totalObjects = buckets.reduce(
    (acc, bucket) => acc + (bucket.objectsCount || 0),
    0
  );
  const heaviestBucket = useMemo(() => {
    if (!buckets.length) return null;
    return [...buckets].sort((a, b) => toBytes(b) - toBytes(a))[0];
  }, [buckets]);

  const handleCreate = async (params) => {
    try {
      await createBucket(
        params.name,
        params.retentionDays,
        params.isPublic,
        params.versioning
      );
      setToastMessage("Bucket criado com sucesso!");
      await loadBuckets();
    } catch (err) {
      console.error("Erro ao criar bucket:", err);
      setToastMessage(`Erro ao criar bucket: ${err.message}`);
    } finally {
      setToastShow(true);
      setCreateModalOpen(false);
    }
  };

  const handleDelete = async (name) => {
    try {
      await deleteBucket(name);
      setToastMessage("Bucket removido com sucesso!");
      await loadBuckets();
    } catch (err) {
      console.error("Erro ao excluir bucket:", err);
      setToastMessage(`Erro ao excluir bucket: ${err.message}`);
    } finally {
      setToastShow(true);
      setConfirmDeleteShow(false);
      setBucketToDelete(null);
    }
  };

  const openEdit = (name) => {
    setEditBucket(buckets.find((bucket) => bucket.name === name));
    setEditModalOpen(true);
  };

  const handleSaveEdit = async (updated) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${import.meta.env.VITE_BACKEND_URL || ""}/api/buckets/${encodeURIComponent(
          updated.originalName || updated.name
        )}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updated),
        }
      );

      if (!res.ok) {
        const body = await res.json();
        throw new Error(body.error || `HTTP ${res.status}`);
      }

      setToastMessage("Bucket atualizado com sucesso!");
      await loadBuckets();
    } catch (err) {
      console.error("Erro ao atualizar bucket:", err);
      setToastMessage(`Erro ao atualizar bucket: ${err.message}`);
    } finally {
      setToastShow(true);
      setEditModalOpen(false);
      setEditBucket(null);
    }
  };

  const handleList = (name) =>
    navigate(`/buckets/${encodeURIComponent(name)}/objects`);

  return (
    <div className="buckets-page">
      <section className="buckets-hero">
        <div>
          <span className="buckets-chip">Storage Workspace</span>
          <h1>Buckets</h1>
          <p>
            Organize seus ambientes de armazenamento, acompanhe exposição pública e
            acesse rapidamente os objetos de cada bucket.
          </p>
        </div>

        <div className="buckets-hero-actions">
          <div className="buckets-search">
            <FiSearch />
            <input
              type="text"
              placeholder="Pesquisar buckets..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>

          <button
            className="btn btn-purple buckets-create-btn"
            onClick={() => setCreateModalOpen(true)}
          >
            <FiPlus />
            Novo Bucket
          </button>
        </div>
      </section>

      <section className="buckets-stats">
        <article className="bucket-stat-card">
          <div className="bucket-stat-icon blue">
            <FiDatabase />
          </div>
          <div>
            <span>Total</span>
            <strong>{buckets.length}</strong>
          </div>
        </article>

        <article className="bucket-stat-card">
          <div className="bucket-stat-icon green">
            <FiGlobe />
          </div>
          <div>
            <span>Públicos</span>
            <strong>{publicCount}</strong>
          </div>
        </article>

        <article className="bucket-stat-card">
          <div className="bucket-stat-icon slate">
            <FiLock />
          </div>
          <div>
            <span>Privados</span>
            <strong>{privateCount}</strong>
          </div>
        </article>

        <article className="bucket-stat-card">
          <div className="bucket-stat-icon amber">
            <FiDatabase />
          </div>
          <div>
            <span>Objetos</span>
            <strong>{totalObjects.toLocaleString("pt-BR")}</strong>
          </div>
        </article>
      </section>

      <section className="buckets-summary-panel">
        <div>
          <span className="summary-label">Maior volume</span>
          <strong>{heaviestBucket?.name || "N/A"}</strong>
          <p>{heaviestBucket?.tamanhoFormatado || "Sem dados"}</p>
        </div>
        <div>
          <span className="summary-label">Resultados</span>
          <strong>{filtered.length}</strong>
          <p>{searchTerm ? "buckets encontrados para sua busca" : "buckets disponíveis"}</p>
        </div>
      </section>

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </Spinner>
        </div>
      ) : filtered.length ? (
        <div className="bucket-card-grid">
          {filtered.map((bucket) => (
            <BucketCard
              key={bucket.name}
              bucket={bucket}
              onList={handleList}
              onSettings={openEdit}
              onDelete={(name) => {
                setBucketToDelete(name);
                setConfirmDeleteShow(true);
              }}
            />
          ))}
        </div>
      ) : (
        <div className="buckets-empty-state">
          <h5>Nenhum bucket encontrado</h5>
          <p>Refine sua busca ou crie um novo bucket para começar.</p>
        </div>
      )}

      <CreateBucketModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreate={handleCreate}
      />
      <EditBucketModal
        show={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditBucket(null);
        }}
        bucket={editBucket}
        onSave={handleSaveEdit}
      />
      <ConfirmDeleteModal
        show={confirmDeleteShow}
        onClose={() => setConfirmDeleteShow(false)}
        onConfirm={() => handleDelete(bucketToDelete)}
        title="Excluir bucket"
        message={`Excluir bucket "${bucketToDelete}"?`}
      />
      <ToastNotification
        show={toastShow}
        message={toastMessage}
        onClose={() => setToastShow(false)}
      />
    </div>
  );
}
